import {
  api_edit_profile_settings_path,
  api_profile_setting_path,
  api_validate_username_path,
} from '@routes';
import { transformToSnakeCase } from '@utils/formatting';

import { AutocompleteOption, AutocompleteRecord } from '../interfaces';
import request from './request';

type entityId = number;
export const ENTITY_TYPE_JOB_LEVEL = 'job_level';
export const ENTITY_TYPE_JOB_FUNCTION = 'job_function';
export const ENTITY_TYPE_ORGANIZATION_INDUSTRY = 'organization_industry';
export const ENTITY_TYPE_COMPANY_SIZE = 'organization_size';
export const ENTITY_TYPE_SUB_ORGANIZATION_SUB_INDUSTRY = 'organization_sub_industry';
export const ENTITY_TYPE_SUB_JOB_FUNCTION = 'sub_job_function';
export const ENTITY_TYPE_ORGANIZATION = 'organization';
export const ENTITY_WORD_TYPE_COMMON = 'common';
export const PROFILE_FIELD_OTHER_VALUE = 'Other';
export const ENTITY_TYPE_JOB_TITLE = 'job_title';
export const PROFILE_OTHER_OPTION: AutocompleteOption = Object.create({
  label: 'Other',
  value: 'Other',
});

export const SOURCE_OPTIONS = [
  'Company corporate subscription',
  'Facebook',
  'Instagram',
  'LinkedIn',
  'Newsletter',
  'Word of mouth',
  'X (Twitter)',
  'Other',
].map((option) => ({ label: option, value: transformToSnakeCase(option) }));

export const JOB_LEVEL_OPTIONS = [
  'C-Level',
  'SVP / EVP',
  'Director / VP',
  'Manager / Supervisor',
  'Mid or Entry Level',
  'Freelance / Contract',
  'Student / Intern',
  'Other',
].map((option) => ({ label: option, value: option }));

export const JOB_FUNCTION_OPTIONS = [
  'Banking/Mortgage',
  'Biotech',
  'Business',
  'Creative/Design',
  'Customer Support/Client Services',
  'Education',
  'Engineering',
  'Executives',
  'Finance/Accounting',
  'Human Resources',
  'IT/Computers/Electronics',
  'Insurance',
  'Journalism/Media/Entertainment',
  'Legal',
  'Logistics/Transportation',
  'Manufacturing/Production/Operations',
  'Marketing',
  'Medical and Health',
  'Retail',
  'Sales',
  'Strategy and Consulting',
  'Other',
].map((option) => ({ label: option, value: option }));

export const ORGANIZATION_INDUSTRY_OPTIONS = [
  'Advertising/Marketing',
  'Aerospace/Aviation',
  'Automotive',
  'Biotech and Pharmaceuticals',
  'Computers and Technology',
  'Corporate Services',
  'Education',
  'Finance',
  'Government',
  'Healthcare/Medical',
  'Insurance',
  'Legal',
  'Media',
  'Non-Profit/Organizations',
  'Retail and Consumer Goods',
  'Telecommunications',
  'Transportation and Logistics',
  'Travel/Hospitality/Entertainment',
  'Utility/Energy',
  'Other',
].map((option) => ({ label: option, value: option }));

export const COMPANY_SIZE_OPTIONS = [
  { label: 'Enterprise: over 1,000 employees', value: 'over 1000' },
  { label: 'Large: 500-999 employees', value: '500 - 999' },
  { label: 'Mid-size: 100-499 employees', value: '100 - 499' },
  { label: 'Small: Less than 100 employees', value: 'less than 100' },
];

// All params are optional because you can save a portion of the profile
export interface SanitizedProfileSettingsParams {
  bio?: string;
  books?: entityId[];
  company_name?: string;
  finding_new_job?: boolean;
  finding_new_job_desc?: string;
  hiring?: boolean;
  hiring_desc?: string;
  instagram?: string;
  interests?: entityId[];
  investing_in_startups?: boolean;
  investing_in_startups_desc?: string;
  linkedin?: string;
  location?: entityId;
  mentioning_disabled?: boolean;
  message_enabled?: boolean;
  name?: string;
  one_line_bio?: string;
  personal_url?: string;
  picture?: string;
  profile_enabled?: boolean;
  providing_services?: boolean;
  providing_services_desc?: string;
  referral_source?: string;
  short_bio?: string;
  shows?: entityId[];
  skills?: entityId[];
  sports_play?: entityId[];
  sports_watch?: entityId[];
  title_at_company?: string;
  topics?: entityId[];
  twitter?: string;
  username?: string;
  withImage?: boolean;
  vacation_spots?: entityId[];
}

export interface Profile {
  avatarUrl: string;
  bio: string;
  company: AutocompleteRecord;
  companySize: string;
  email: string;
  favoriteBooks: AutocompleteRecord[];
  favoriteShows: AutocompleteRecord[];
  favoriteSportsToPlay: AutocompleteRecord[];
  favoriteSportsToWatch: AutocompleteRecord[];
  favoriteVacationSpots: AutocompleteRecord[];
  findingNewJob: boolean;
  findingNewJobDesc: string;
  hiring: boolean;
  hiringDesc: string;
  id: number;
  instagram: string;
  interests: AutocompleteRecord[];
  investingInStartups: boolean;
  investingInStartupsDesc: string;
  isStaff: boolean;
  isSubscriber: boolean;
  jobFunction: string;
  jobFunctionEntity: AutocompleteRecord;
  jobLevel: string;
  jobLevelEntity: AutocompleteRecord;
  linkedin: string;
  location: AutocompleteRecord;
  mentioningDisabled: boolean;
  message?: string;
  messageEnabled: boolean;
  messagingBlocked: boolean;
  name: string;
  oneLineBio: string;
  organizationIndustry: string;
  organizationIndustryEntity: AutocompleteRecord;
  organizationSize: AutocompleteRecord;
  organizationSubIndustry: string;
  organizationSubIndustryEntity: AutocompleteRecord;
  personalUrl: string;
  postalCode: string;
  profileEnabled: boolean;
  providingServices: boolean;
  providingServicesDesc: string;
  publicProfile: boolean;
  shortBio: string;
  skills: AutocompleteRecord[];
  subJobFunction: string;
  subJobFunctionEntity: AutocompleteRecord;
  titleAtCompany: AutocompleteRecord;
  topics: AutocompleteRecord[];
  twitter: string;
  userId: number;
  username: string;
  emailVerificationRequired: boolean;
}

export async function fetchProfileSettings(): Promise<Profile> {
  const resp = await request('get', api_edit_profile_settings_path());
  return resp;
}

export async function updateProfileSettings(
  userId: number | string,
  values: SanitizedProfileSettingsParams
): Promise<Profile> {
  const { withImage, ...sanitizedValues } = values;
  const formData = new FormData();
  if (withImage) {
    for (const key in sanitizedValues) {
      formData.append(key, sanitizedValues[key]);
    }
  }

  const resp = await request(
    'put',
    api_profile_setting_path(userId),
    withImage ? formData : sanitizedValues,
    withImage
  );
  return resp;
}

export async function checkUsername(value: { username: string }): Promise<{ success: boolean }> {
  const resp = await request('get', api_validate_username_path(value));
  return resp;
}
